<template>
  <section class="page">
    <div class="eyebrow">📋 病历生成 · 结构化输出</div>
    <div class="heading">
      <div>
        <h1>病历生成 · <em>按规范书写 · 一键生成</em></h1>
        <p>国家《病历书写基本规范》格式，自动分段 + 7 项必要章节完整性自检 + 标签云摘要</p>
      </div>
    </div>

    <!-- 病历类型 -->
    <div class="section-title" style="margin-top:18px">病历类型</div>
    <div class="chips">
      <button
        v-for="t in types"
        :key="t"
        class="chip"
        :class="{ active: type === t }"
        type="button"
        @click="type = t"
      >{{ t }}</button>
    </div>

    <!-- 采集表单 -->
    <div class="record-form">
      <div class="row">
        <input v-model="department" class="ai-input" placeholder="科室，如：心内科" :disabled="loading">
        <input v-model="complaint" class="ai-input" placeholder="主诉（选填），如：胸痛 3 小时" :disabled="loading">
      </div>
      <input v-model="history" class="ai-input" placeholder="已知病史 / 既往史（选填），如：高血压 10 年、吸烟 20 年" :disabled="loading">
      <textarea
        v-model="dialog"
        class="ai-input"
        placeholder="医患对话记录 *必填 —— 粘贴问诊对话，AI 将提取信息生成规范病历"
        :disabled="loading"
      />
      <div class="row">
        <span class="hint">💡 病历生成后将自动拆分为主诉 / 现病史 / 查体 / 诊断 等结构化区块</span>
        <button class="btn" :disabled="loading || !dialog.trim()" @click="generate">{{ loading ? '生成中…' : '📋 生成规范病历' }}</button>
      </div>
    </div>

    <gate-banner :error="gateError" />

    <empty-state
      v-if="!result && !loading && !error"
      icon="📋"
      title="填写病历信息 · 一键生成规范病历"
      description="系统将按国家《病历书写基本规范》自动分段，完整性自检，确保 7 项必要章节（主诉·现病史·既往史·体格检查·辅助检查·诊断·治疗计划）全部覆盖…"
    />

    <ai-output-card v-else title="📄 生成的病历" :loading="loading" :error="error" :model="result && result.model" :mock="result && result.mock" :warning="result && result.warning">
      <template v-if="parsed">
        <!-- 完整性自检 -->
        <div class="section-title">完整性自检 <small :class="allPass ? 'pass' : 'fail'">{{ passedCount }}/7 章节覆盖</small></div>
        <section-checklist :items="parsed.checklist || []" />

        <!-- 分节病历 -->
        <div class="section-title" style="margin-top:20px">病历正文</div>
        <article v-for="(s, i) in parsed.sections || []" :key="i" class="record-section">
          <h3>{{ s.title }}</h3>
          <p>{{ s.content }}</p>
        </article>

        <!-- 标签云摘要 -->
        <div class="section-title" style="margin-top:20px">摘要标签</div>
        <tag-cloud :tags="parsed.tags || []" />
      </template>
      <template v-else-if="result && result.content"><p style="white-space: pre-wrap">{{ result.content }}</p></template>
    </ai-output-card>
  </section>
</template>

<script>
import { callAI } from '../api';
import AIOutputCard from '../components/AIOutputCard.vue';
import GateBanner from '../components/GateBanner.vue';
import EmptyState from '../components/EmptyState.vue';
import SectionChecklist from '../components/SectionChecklist.vue';
import TagCloud from '../components/TagCloud.vue';

export default {
  name: 'RecordView',
  components: { AIOutputCard, GateBanner, EmptyState, SectionChecklist, TagCloud },
  data: () => ({
    types: ['📄 入院记录', '📝 首次病程', '⚕️ 手术记录', '✏️ 自定义'],
    type: '📄 入院记录',
    department: '',
    complaint: '',
    history: '',
    dialog: '',
    loading: false,
    error: '',
    gateError: null,
    result: null,
  }),
  computed: {
    parsed() { return this.result && this.result.parsed; },
    passedCount() { return ((this.parsed && this.parsed.checklist) || []).filter((c) => c.present).length; },
    allPass() { return this.passedCount === 7; },
  },
  methods: {
    async generate() {
      if (!this.dialog.trim() || this.loading) return;
      this.loading = true;
      this.error = '';
      try {
        this.result = await callAI('record', {
          type: this.type.replace(/^[^\s]+\s*/, ''), // 去掉 emoji 前缀
          department: this.department.trim(),
          complaint: this.complaint.trim(),
          history: this.history.trim(),
          dialog: this.dialog.trim(),
        });
      } catch (e) {
        this.gateError = { code: e.code, msg: e.msg || e.message };
        this.error = this.gateError.msg;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.chip.active { color: var(--text-1); border-color: var(--primary-2); background: var(--panel-1); font-weight: 600; }
.record-form { margin-top: 12px; display: grid; gap: 10px; }
.record-form .row { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; }
.record-form .row .hint { flex: 1; min-width: 200px; }
.record-section { border: 1px solid var(--border-2); border-radius: var(--radius); padding: 14px 16px; margin-bottom: 10px; background: var(--bg-2); transition: border-color 0.2s; }
.record-section:hover { border-color: var(--border-hi); }
.record-section h3 { margin: 0 0 8px; font-size: 13px; color: var(--accent); }
.record-section p { margin: 0; font-size: 13px; line-height: 1.9; color: var(--text-1); white-space: pre-wrap; }
.section-title small.pass { color: var(--success); }
.section-title small.fail { color: var(--danger); }
</style>
